import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useUpdateCaseDescription } from '@/hooks/use-case-queries'

export interface CaseDescriptionEditor {
  /** Description shown in the UI: the in-progress draft, else the persisted value. */
  description: string
  isEditing: boolean
  isSaving: boolean
  startEditing: () => void
  cancelEditing: () => void
  setDraft: (value: string) => void
  save: () => void
}

/**
 * Encapsulates the case-summary description edit flow for the report page:
 * draft-vs-persisted resolution, edit/cancel toggling, and the save mutation
 * (with success toast + state reset). Keeps the page container free of the
 * description state machine.
 */
export function useCaseDescriptionEditor(
  caseId: string,
  persistedDescription: string | null | undefined
): CaseDescriptionEditor {
  const { t } = useTranslation()
  const [draftDescription, setDraftDescription] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const updateDescription = useUpdateCaseDescription(caseId)

  const description = draftDescription ?? persistedDescription ?? ''

  const startEditing = useCallback(() => setIsEditing(true), [])

  const cancelEditing = useCallback(() => {
    setIsEditing(false)
    setDraftDescription(null)
  }, [])

  const save = useCallback(() => {
    updateDescription.mutate(description.trim(), {
      onSuccess: () => {
        toast.success(t('common.save'))
        setIsEditing(false)
        setDraftDescription(null)
      },
    })
  }, [description, updateDescription, t])

  return {
    description,
    isEditing,
    isSaving: updateDescription.isPending,
    startEditing,
    cancelEditing,
    setDraft: setDraftDescription,
    save,
  }
}
